import { normalizeForComparison } from "./apiDataProcessing";

/**
 * Finds a brand and optional subbrand by name using normalized string comparison
 * Performs case-insensitive, whitespace-tolerant matching against brandOptions
 * Returns null if brand not found, or brand/subbrand objects with their values
 *
 * @param {string} brandName - The brand name to search for
 * @param {string|null} [subbrandName] - Optional subbrand name to search for within the brand
 * @returns {object|null} Object containing brand, subbrand (if found), and value, or null if not found
 *
 * @example
 * const result = findBrandByName("Coca Cola", "Diet Coke");
 * // Returns: { brand: {...}, subbrand: {...}, value: "Coca Cola_Diet Coke" }
 *
 * const brandOnly = findBrandByName("Pepsi");
 * // Returns: { brand: {...}, subbrand: null, value: "Pepsi" }
 */
export const findBrandByName = (
	brandName: string,
	brandOptions: any[],
	subbrandName?: string | null
) => {
	if (!brandName || !brandOptions || brandOptions.length === 0) {
		return null;
	}
	const normalizedBrandName = normalizeForComparison(brandName);
	// Exact match on the normalized brand title
	const targetBrand = (brandOptions as any[]).find(
		(b) => normalizeForComparison(b.title) === normalizedBrandName
	);
	if (!targetBrand) {
		return null;
	}
	if (!subbrandName) {
		return { brand: targetBrand, subbrand: null, value: targetBrand.value };
	}
	// A brand with no subbrands falls back to the parent
	if (!targetBrand.children || targetBrand.children.length === 0) {
		return { brand: targetBrand, subbrand: null, value: targetBrand.value };
	}

	const normalizedSubbrandName = normalizeForComparison(subbrandName);

	// Exact match on the normalized subbrand title
	const targetSubbrand = targetBrand.children.find(
		(sub: any) => normalizeForComparison(sub.title) === normalizedSubbrandName
	);
	if (!targetSubbrand) {
		return { brand: targetBrand, subbrand: null, value: targetBrand.value };
	}

	return {
		brand: targetBrand,
		subbrand: targetSubbrand,
		value: targetSubbrand.value,
	};
};

/**
 * Intelligently merges complete subbrand selections to their parent brand
 * When all subbrands of a parent brand are selected, replaces them with just the parent brand
 * This optimizes selections by reducing redundancy and improving user experience
 *
 * @param {string[]} selectedBrands - Array of selected brand/subbrand values
 * @returns {string[]} Optimized array with complete subbrand sets merged to parent brands
 *
 * @example
 * // If "Coca Cola" has subbrands "Diet Coke", "Coke Zero", "Sprite"
 * // and all are selected along with other brands:
 * const selected = ["Coca Cola_Diet Coke", "Coca Cola_Coke Zero", "Coca Cola_Sprite", "Pepsi"];
 * const merged = mergeCompleteSubbrandsToParent(selected);
 * // Returns: ["Coca Cola", "Pepsi"] - subbrands merged to parent
 */
export const mergeCompleteSubbrandsToParent = (
	selectedBrands: string[],
	brandOptions: any[]
): string[] => {
	if (!selectedBrands || selectedBrands.length === 0) return selectedBrands;

	// Selected subbrands grouped under their parent brand
	const brandGroups = new Map<
		string,
		{
			parentValue: string;
			parentTitle: string;
			allChildren: string[];
			selectedChildren: string[];
		}
	>();

	// Selections tracked separately by level
	const directParentSelections = new Set<string>();
	const childSelections = new Set<string>();

	// First pass: classify every selection as a parent brand or a subbrand
	selectedBrands.forEach((selectedValue) => {
		brandOptions.forEach((brand: any) => {
			if (brand.value === selectedValue) {
				directParentSelections.add(selectedValue);
				return;
			}

			if (brand.children) {
				const foundChild = brand.children.find(
					(child: any) => child.value === selectedValue
				);
				if (foundChild) {
					childSelections.add(selectedValue);

					if (!brandGroups.has(brand.value)) {
						brandGroups.set(brand.value, {
							parentValue: brand.value,
							parentTitle: brand.title,
							allChildren: brand.children.map((c: any) => c.value),
							selectedChildren: [],
						});
					}
					brandGroups.get(brand.value)!.selectedChildren.push(selectedValue);
				}
			}
		});
	});

	// Second pass: decide which groups collapse into their parent
	const result = new Set<string>();

	directParentSelections.forEach((parentValue) => {
		result.add(parentValue);
	});

	brandGroups.forEach((group, parentValue) => {
		const hasAllChildren =
			group.allChildren.length === group.selectedChildren.length &&
			group.allChildren.every((child) =>
				group.selectedChildren.includes(child)
			);

		if (hasAllChildren) {
			// Every subbrand is selected, so the parent stands in for all of them
			result.add(parentValue);
			console.log(
				`[mergeCompleteSubbrandsToParent] Merged ${group.selectedChildren.length} subbrands to parent "${group.parentTitle}"`
			);
		} else {
			// A partial selection stays expanded
			group.selectedChildren.forEach((child) => {
				result.add(child);
			});
		}
	});

	const mergedResult = Array.from(result);

	if (mergedResult.length !== selectedBrands.length) {
		console.log(`[mergeCompleteSubbrandsToParent] Optimized selection:`, {
			before: selectedBrands,
			after: mergedResult,
			savings: selectedBrands.length - mergedResult.length,
		});
	}

	return mergedResult;
};

/**
 * Gets human-readable display names for selected brand values
 * Intelligently handles parent/child relationships - shows parent brand names instead of
 * subbrand names when parent is selected, otherwise shows individual subbrand names
 *
 * @param {string[]} selectedValues - Array of selected brand/subbrand value strings
 * @returns {string[]} Array of display names for UI presentation
 *
 * @example
 * // If "Coca Cola" is selected as parent:
 * getSelectedBrandNames(["Coca Cola", "Pepsi_Diet Pepsi"]);
 * // Returns: ["Coca Cola", "Diet Pepsi"]
 *
 * // If only subbrands selected:
 * getSelectedBrandNames(["Coca Cola_Diet Coke", "Coca Cola_Sprite"]);
 * // Returns: ["Diet Coke", "Sprite"]
 */
export const getSelectedBrandNames = (
	selectedValues: string[],
	brandOptions: any[]
) => {
	// console.log('[getSelectedBrandNames] Processing selected values:', selectedValues);

	// Matches stored per level so parent selections can shadow their subbrands
	const selectedBrandsAndSubbrands = {
		brands: [] as any[], // matched parent brands
		subbrands: [] as any[], // matched subbrands
		parentToChild: {} as any, // brand-to-subbrand mapping
	};

	if (selectedValues && selectedValues.length > 0) {
		selectedValues.forEach((value: string) => {
			let foundMatch = false;

			brandOptions.forEach((brand: any) => {
				// 1. Direct match on a parent brand
				if (brand.value === value) {
					selectedBrandsAndSubbrands.brands.push({
						value: brand.value,
						title: brand.title,
						type: "brand",
					});
					foundMatch = true;
					// console.log('[getSelectedBrandNames] Found brand (parent):', brand.title);
				}
				// 2. Match on one of the brand's subbrands
				else if (brand.children) {
					const foundSubbrand = brand.children.find(
						(subbrand: any) => subbrand.value === value
					);
					if (foundSubbrand) {
						selectedBrandsAndSubbrands.subbrands.push({
							value: foundSubbrand.value,
							title: foundSubbrand.title,
							parentBrand: brand.title,
							type: "subbrand",
						});
						foundMatch = true;
						// console.log('[getSelectedBrandNames] Found subbrand (child):', foundSubbrand.title, 'under', brand.title);
					}
				}
			});

			if (!foundMatch) {
				console.warn(
					"[getSelectedBrandNames] No match found for value:",
					value
				);
			}
		});
	}

	// Display rule: a selected parent brand stands alone, and a selected subbrand
	// shows only while its parent is not selected

	const displayNames: string[] = [];

	const selectedParentBrands = selectedBrandsAndSubbrands.brands.map(
		(b) => b.title
	);

	selectedBrandsAndSubbrands.brands.forEach((brand) => {
		displayNames.push(brand.title);
	});

	selectedBrandsAndSubbrands.subbrands.forEach((subbrand) => {
		if (!selectedParentBrands.includes(subbrand.parentBrand)) {
			displayNames.push(subbrand.title);
		}
		//  else {
		//   console.log('[getSelectedBrandNames] Skipping subbrand because parent is selected:', subbrand.title, 'parent:', subbrand.parentBrand);
		// }
	});

	// console.log('[getSelectedBrandNames] Input tempSelectedBrands:', selectedValues);
	// console.log('[getSelectedBrandNames] Final display names:', displayNames);
	// console.log('[getSelectedBrandNames] Selected brands (parents):', selectedBrandsAndSubbrands.brands);
	// console.log('[getSelectedBrandNames] Selected subbrands (children):', selectedBrandsAndSubbrands.subbrands);

	return displayNames;
};
